import { StoryblokImage, storyblokImageLoader } from '@cinch-nx/data-storyblok'
import cx from 'classnames'
import Image from 'next/image'
import Link from 'next/link'

import styles from './marketing-card.module.css'

interface MarketingCardProps {
  desktopImage: StoryblokImage
  mobileImage: StoryblokImage
  campaignName: string
  url: string
  onClick?: () => void
}

const FALLBACK_DIMENSIONS = {
  desktop: { width: 1064, height: 262 },
  mobile: { width: 327, height: 407 },
}

const FALLBACK_ALT_TEXT = 'Marketing campaign banner'

/**
 * Extracts the intrinsic pixel dimensions embedded in a Storyblok asset URL
 * (`https://a.storyblok.com/f/{space}/{width}x{height}/{hash}/{name}`), so
 * next/image can reserve space with the asset's real aspect ratio before it
 * loads.
 * @param filename - The Storyblok asset URL.
 * @param fallback - Dimensions to use when the URL doesn't contain a
 * `{width}x{height}` segment.
 * @returns The parsed `{ width, height }`, or the fallback.
 */
const getImageDimensions = (
  filename: string,
  fallback: { width: number; height: number },
) => {
  const match = filename.match(/\/(\d+)x(\d+)\//)

  if (!match) {
    return fallback
  }

  return { width: Number(match[1]), height: Number(match[2]) }
}

const MarketingCard = ({
  desktopImage,
  mobileImage,
  url,
  onClick,
}: MarketingCardProps) => {
  if (!desktopImage?.filename || !mobileImage?.filename) {
    return null
  }

  return (
    <Link href={url} className={styles.card} onClick={onClick}>
      <Image
        className={cx(styles.image, styles.mobileImage)}
        src={mobileImage.filename}
        alt={mobileImage.alt ?? FALLBACK_ALT_TEXT}
        loader={storyblokImageLoader}
        sizes="100vw"
        {...getImageDimensions(
          mobileImage.filename,
          FALLBACK_DIMENSIONS.mobile,
        )}
      />
      <Image
        className={cx(styles.image, styles.desktopImage)}
        src={desktopImage.filename}
        alt={desktopImage.alt ?? FALLBACK_ALT_TEXT}
        loader={storyblokImageLoader}
        sizes="(max-width: 1064px) 100vw, 1064px"
        {...getImageDimensions(
          desktopImage.filename,
          FALLBACK_DIMENSIONS.desktop,
        )}
      />
    </Link>
  )
}

export default MarketingCard
